import { Image as ImageIcon, Upload, X } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { cn } from '@/libs/utils'

interface ImageUploadProps {
  value?: string
  onChange: (url: string | undefined) => void
  onFileSelect?: (file: File) => void
  disabled?: boolean
  className?: string
  maxSizeMB?: number
  acceptedTypes?: string[]
}

const getDropzoneClassName = (isDragging: boolean, disabled: boolean, error: string | null) =>
  cn(
    'relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors cursor-pointer',
    'min-h-[160px] bg-muted/30',
    isDragging && 'border-primary bg-primary/10',
    !isDragging && !disabled && 'border-border hover:border-primary/50 hover:bg-muted/50',
    disabled && 'cursor-not-allowed opacity-50',
    error && 'border-destructive',
  )

interface ImagePreviewProps {
  value: string
  disabled: boolean
  onRemove: (e: React.MouseEvent) => void
}

function ImagePreview({ value, disabled, onRemove }: ImagePreviewProps) {
  const { t } = useTranslation()

  return (
    <>
      <img
        src={value}
        alt={t('imageUpload.preview')}
        className="max-h-40 max-w-full rounded-md object-contain"
      />
      {!disabled && (
        <Button
          type="button"
          variant="destructive"
          size="icon"
          className="absolute top-2 right-2 h-7 w-7"
          onClick={onRemove}
          aria-label={t('imageUpload.removeImage')}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </>
  )
}

interface UploadPromptProps {
  isDragging: boolean
  maxSizeMB: number
}

function UploadPrompt({ isDragging, maxSizeMB }: UploadPromptProps) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col items-center gap-2 text-muted-foreground p-4">
      {isDragging ? (
        <ImageIcon className="h-10 w-10 text-primary" aria-hidden="true" />
      ) : (
        <Upload className="h-10 w-10" aria-hidden="true" />
      )}
      <div className="text-center">
        <p className="text-sm font-medium">
          {isDragging ? t('imageUpload.dropHere') : t('imageUpload.dragOrClick')}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {t('imageUpload.maxSize', { size: maxSizeMB })}
        </p>
      </div>
    </div>
  )
}

export function ImageUpload({
  value,
  onChange,
  onFileSelect,
  disabled = false,
  className,
  maxSizeMB = 5,
  acceptedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
}: ImageUploadProps) {
  const { t } = useTranslation()
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFile = useCallback(
    (file: File) => {
      setError(null)

      if (!acceptedTypes.includes(file.type)) {
        setError(t('imageUpload.invalidType'))
        return
      }

      if (file.size > maxSizeMB * 1024 * 1024) {
        setError(t('imageUpload.tooLarge', { size: maxSizeMB }))
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        onChange(e.target?.result as string)
      }
      reader.readAsDataURL(file)

      onFileSelect?.(file)
    },
    [acceptedTypes, maxSizeMB, onChange, onFileSelect, t],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)

      if (disabled) return

      const file = e.dataTransfer.files[0]
      if (file) handleFile(file)
    },
    [disabled, handleFile],
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      if (!disabled) setIsDragging(true)
    },
    [disabled],
  )

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleClick = () => {
    if (!disabled) inputRef.current?.click()
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
  }

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(undefined)
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={value ? t('imageUpload.changeImage') : t('imageUpload.uploadImage')}
        aria-describedby={error ? 'image-upload-error' : undefined}
        aria-disabled={disabled}
        onClick={handleClick}
        onKeyDown={(e) => e.key === 'Enter' && handleClick()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={getDropzoneClassName(isDragging, disabled, error)}
      >
        {value ? (
          <ImagePreview value={value} disabled={disabled} onRemove={handleRemove} />
        ) : (
          <UploadPrompt isDragging={isDragging} maxSizeMB={maxSizeMB} />
        )}
        <input
          ref={inputRef}
          type="file"
          accept={acceptedTypes.join(',')}
          onChange={handleInputChange}
          disabled={disabled}
          className="sr-only"
          aria-hidden="true"
        />
      </div>
      {error && (
        <p id="image-upload-error" className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
